import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Tabs,
  Tab,
  Divider,
  ListSubheader
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

const AddKeywordDialog = ({ open, onClose, onAddKeyword }) => {
  const [keywordType, setKeywordType] = useState('single');
  const [keywordInput, setKeywordInput] = useState('');
  const [bulkKeywords, setBulkKeywords] = useState('');
  const [status, setStatus] = useState('active');
  const [category, setCategory] = useState('');

  const handleTabChange = (event, newValue) => {
    setKeywordType(newValue);
  };

  const handleSubmit = () => {
    const keywordData = {
      keywordType,
      status,
      category
    };

    if (keywordType === 'single') {
      keywordData.keyword = keywordInput;
    } else {
      keywordData.bulkKeywords = bulkKeywords;
    }

    onAddKeyword(keywordData);
    
    // Reset form
    setKeywordInput('');
    setBulkKeywords('');
    setStatus('active');
    setCategory('');
    setKeywordType('single');
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: '12px' }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
          Add Keywords
        </Typography>
      </DialogTitle>
      <Divider />
      
      <DialogContent>
        <Tabs 
          value={keywordType} 
          onChange={handleTabChange} 
          sx={{ mb: 3 }}
          variant="fullWidth"
        >
          <Tab value="single" label="Single Keyword" />
          <Tab value="bulk" label="Bulk Import" />
        </Tabs>
        
        {keywordType === 'single' ? (
          <TextField
            label="Keyword"
            fullWidth
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            margin="normal"
            placeholder="Enter keyword"
            helperText="Enter a single keyword to track"
          />
        ) : (
          <TextField
            label="Bulk Keywords"
            fullWidth
            multiline
            rows={6}
            value={bulkKeywords}
            onChange={(e) => setBulkKeywords(e.target.value)}
            margin="normal"
            placeholder="Enter one keyword per line"
            helperText="Enter multiple keywords, one per line"
          />
        )}
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
          <FormControl sx={{ minWidth: '200px', flex: 1 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              label="Status"
              onChange={(e) => setStatus(e.target.value)}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="paused">Paused</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl sx={{ minWidth: '200px', flex: 1 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={category}
              label="Category"
              onChange={(e) => setCategory(e.target.value)}
            >
              <MenuItem value="">None</MenuItem>
              
              <ListSubheader>Business & Finance</ListSubheader>
              <MenuItem value="business">Business</MenuItem>
              <MenuItem value="finance">Finance</MenuItem>
              <MenuItem value="investing">Investing</MenuItem>
              <MenuItem value="banking">Banking</MenuItem>
              <MenuItem value="insurance">Insurance</MenuItem>
              <MenuItem value="careers">Careers & Employment</MenuItem>
              
              <ListSubheader>Technology</ListSubheader>
              <MenuItem value="technology">Technology</MenuItem>
              <MenuItem value="computing">Computing</MenuItem>
              <MenuItem value="internet">Internet</MenuItem>
              <MenuItem value="software">Software</MenuItem>
              <MenuItem value="electronics">Electronics</MenuItem>
              <MenuItem value="mobile">Mobile & Wireless</MenuItem>
              
              <ListSubheader>Health & Wellness</ListSubheader>
              <MenuItem value="health">Health</MenuItem>
              <MenuItem value="medicine">Medicine</MenuItem>
              <MenuItem value="fitness">Fitness</MenuItem>
              <MenuItem value="nutrition">Nutrition</MenuItem>
              <MenuItem value="mental_health">Mental Health</MenuItem>
              <MenuItem value="diseases">Diseases & Conditions</MenuItem>
              
              <ListSubheader>Lifestyle</ListSubheader>
              <MenuItem value="lifestyle">Lifestyle</MenuItem>
              <MenuItem value="fashion">Fashion</MenuItem>
              <MenuItem value="beauty">Beauty</MenuItem>
              <MenuItem value="food">Food & Dining</MenuItem>
              <MenuItem value="home">Home & Garden</MenuItem>
              <MenuItem value="parenting">Parenting</MenuItem>
              <MenuItem value="relationships">Relationships</MenuItem>
              
              <ListSubheader>Entertainment</ListSubheader>
              <MenuItem value="entertainment">Entertainment</MenuItem>
              <MenuItem value="movies">Movies</MenuItem>
              <MenuItem value="music">Music</MenuItem>
              <MenuItem value="television">Television</MenuItem>
              <MenuItem value="books">Books & Literature</MenuItem>
              <MenuItem value="gaming">Gaming</MenuItem>
              <MenuItem value="celebrities">Celebrities</MenuItem>
              
              <ListSubheader>Travel & Transportation</ListSubheader>
              <MenuItem value="travel">Travel</MenuItem>
              <MenuItem value="destinations">Destinations</MenuItem>
              <MenuItem value="hotels">Hotels & Accommodations</MenuItem>
              <MenuItem value="transportation">Transportation</MenuItem>
              <MenuItem value="automotive">Automotive</MenuItem>
              <MenuItem value="aviation">Aviation</MenuItem>
              
              <ListSubheader>Shopping & Retail</ListSubheader>
              <MenuItem value="shopping">Shopping</MenuItem>
              <MenuItem value="ecommerce">E-commerce</MenuItem>
              <MenuItem value="consumer_goods">Consumer Goods</MenuItem>
              <MenuItem value="luxury">Luxury Items</MenuItem>
              <MenuItem value="discounts">Discounts & Deals</MenuItem>
              
              <ListSubheader>Real Estate & Property</ListSubheader>
              <MenuItem value="real_estate">Real Estate</MenuItem>
              <MenuItem value="residential">Residential Property</MenuItem>
              <MenuItem value="commercial">Commercial Property</MenuItem>
              <MenuItem value="rental">Rental Property</MenuItem>
              <MenuItem value="mortgage">Mortgages & Loans</MenuItem>
              
              <ListSubheader>Education & Reference</ListSubheader>
              <MenuItem value="education">Education</MenuItem>
              <MenuItem value="schools">Schools & Universities</MenuItem>
              <MenuItem value="online_learning">Online Learning</MenuItem>
              <MenuItem value="reference">Reference</MenuItem>
              <MenuItem value="science">Science</MenuItem>
              <MenuItem value="history">History</MenuItem>
              
              <ListSubheader>Sports & Recreation</ListSubheader>
              <MenuItem value="sports">Sports</MenuItem>
              <MenuItem value="outdoor">Outdoor Activities</MenuItem>
              <MenuItem value="fitness_activities">Fitness Activities</MenuItem>
              <MenuItem value="team_sports">Team Sports</MenuItem>
              <MenuItem value="individual_sports">Individual Sports</MenuItem>
              
              <ListSubheader>News & Current Events</ListSubheader>
              <MenuItem value="news">News</MenuItem>
              <MenuItem value="politics">Politics</MenuItem>
              <MenuItem value="weather">Weather</MenuItem>
              <MenuItem value="world_news">World News</MenuItem>
              <MenuItem value="local_news">Local News</MenuItem>
              
              <ListSubheader>Other Categories</ListSubheader>
              <MenuItem value="arts">Arts & Culture</MenuItem>
              <MenuItem value="religion">Religion & Spirituality</MenuItem>
              <MenuItem value="legal">Legal</MenuItem>
              <MenuItem value="environment">Environment</MenuItem>
              <MenuItem value="pets">Pets & Animals</MenuItem>
              <MenuItem value="hobbies">Hobbies & Interests</MenuItem>
              <MenuItem value="general">General</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} variant="outlined">Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          startIcon={<AddIcon />}
          disabled={(keywordType === 'single' && keywordInput.trim() === '') || 
                   (keywordType === 'bulk' && bulkKeywords.trim() === '')}
        >
          Add Keywords
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddKeywordDialog; 